import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Trophy, RotateCcw, Home, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useLobbySocket } from "../hooks/useLobbySocket";
import { toast } from "sonner";
import { UserName } from "../components/UserName";

function PodiumSpot({ rank, player }) {
  const heights = { 1: 192, 2: 132, 3: 100 };
  const colors = {
    1: "bg-[var(--accent)] text-black shadow-[0_-10px_50px_-5px_var(--accent)]",
    2: "bg-zinc-300 text-zinc-900",
    3: "bg-amber-500 text-amber-950",
  };
  const initial = (player?.username || "?")[0]?.toUpperCase();
  if (!player) {
    return <div className="w-28" style={{ height: heights[rank] }}><div className="w-full h-full rounded-t-2xl bg-zinc-800/30" /></div>;
  }
  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: (4 - rank) * 0.15, type: "spring", stiffness: 220, damping: 18 }}
      data-testid={`podium-${rank}`}
      className="flex flex-col items-center"
    >
      <div className="w-14 h-14 rounded-full bg-[var(--surface-2)] border-2 border-white/10 flex items-center justify-center text-xl font-black text-zinc-300 mb-2">
        {initial}
      </div>
      <div className="text-xs uppercase tracking-widest text-zinc-400 mb-0.5 max-w-[8rem] truncate"><UserName user={player} /></div>
      <div className="text-2xl font-black tabular-nums mb-2">{player.score ?? player.total}</div>
      <div className={`w-28 rounded-t-2xl flex items-center justify-center font-black text-3xl ${colors[rank]}`} style={{ height: heights[rank] }}>
        {rank === 1 && <Trophy size={28} strokeWidth={2.5} className="mr-1" />}
        {rank}
      </div>
    </motion.div>
  );
}

export default function LobbyResults() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lobby, setLobby] = useState(null);
  const [reopenBanner, setReopenBanner] = useState(null);  // { by_username }
  const [busy, setBusy] = useState(false);
  const navigatingRef = useRef(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`footguesser_results_${code}`);
      if (raw) setData(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [code]);

  // Fetch lobby snapshot (so we know is_ranked + host_user_id).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: lb } = await api.get(`/lobbies/${code}`);
        if (!cancelled) setLobby(lb);
      } catch { /* ignore — lobby may be gone */ }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const isHost = lobby && user && lobby.host_user_id === user.id;
  const isRanked = !!lobby?.is_ranked;

  // Keep the lobby WS open so we receive `lobby_reopened`.
  const handleEvent = useCallback((evt) => {
    if (evt.type === "lobby_reopened") {
      const p = evt.payload || {};
      if (navigatingRef.current) return;
      // Host already navigated in the click handler. Other members see banner.
      if (user && p.by_user_id && p.by_user_id === user.id) {
        navigatingRef.current = true;
        navigate(`/lobby/${code}`, { replace: true });
        return;
      }
      setReopenBanner({ by_username: p.by_username || "Host" });
    }
    if (evt.type === "lobby_state") {
      // Sync host + ranked flags if changed.
      const p = evt.payload || {};
      if (p.lobby_code) {
        setLobby((prev) => ({ ...(prev || {}), ...p }));
      }
    }
  }, [code, navigate, user]);

  useLobbySocket(code, handleEvent);

  // Confetti once on mount if user is winner
  useEffect(() => {
    if (!data) return;
    const myRow = (data.final_leaderboard || []).find((r) => r.user_id === user?.id);
    if (myRow?.rank === 1) {
      confetti({ particleCount: 160, spread: 110, origin: { x: 0.5, y: 0.55 }, colors: ["#00FF87", "#ffffff"] });
      setTimeout(() => confetti({ particleCount: 80, spread: 90, origin: { x: 0.2, y: 0.6 }, colors: ["#00FF87", "#ffffff"] }), 220);
      setTimeout(() => confetti({ particleCount: 80, spread: 90, origin: { x: 0.8, y: 0.6 }, colors: ["#00FF87", "#ffffff"] }), 320);
    }
  }, [data, user?.id]);

  const playAgain = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/lobbies/${code}/play-again`);
      // Host navigates immediately; non-hosts will get `lobby_reopened` WS event.
      navigatingRef.current = true;
      navigate(`/lobby/${code}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restart lobby");
      setBusy(false);
    }
  };

  const stayInLobby = () => {
    navigatingRef.current = true;
    navigate(`/lobby/${code}`, { replace: true });
  };

  const leaveLobby = async () => {
    try { await api.post(`/lobbies/${code}/leave`); } catch { /* idempotent */ }
    navigate("/dashboard", { replace: true });
  };

  const lb = useMemo(() => (data?.final_leaderboard || []).slice(), [data]);
  const podium = useMemo(() => ({
    1: lb.find((r) => r.rank === 1) ? { ...lb.find((r) => r.rank === 1), score: lb.find((r) => r.rank === 1).total } : null,
    2: lb.find((r) => r.rank === 2) ? { ...lb.find((r) => r.rank === 2), score: lb.find((r) => r.rank === 2).total } : null,
    3: lb.find((r) => r.rank === 3) ? { ...lb.find((r) => r.rank === 3), score: lb.find((r) => r.rank === 3).total } : null,
  }), [lb]);
  const myRow = lb.find((r) => r.user_id === user?.id);

  if (!data) {
    return (
      <div data-testid="results-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-3xl font-bold mb-3">No results to show</div>
          <div className="text-sm text-zinc-400 mb-6">Open this page after a game ends to see the podium.</div>
          <Link to="/dashboard" className="px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-semibold">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="results-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <header className="relative px-6 py-5 max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">← dashboard</Link>
        <div className="text-xs uppercase tracking-widest text-zinc-500">Game over</div>
      </header>

      <main className="relative max-w-4xl mx-auto px-6 pb-16 space-y-12 text-center">
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">final standings</div>
          <h1 className="text-5xl sm:text-6xl font-black mt-3">
            {myRow?.rank === 1 ? <>You <span className="text-[var(--accent)]">won</span>.</> : "Game complete."}
          </h1>
          <p className="text-sm text-zinc-400 mt-3">
            {myRow ? <>You finished <span className="text-[var(--accent)] font-bold">#{myRow.rank}</span> with <span className="font-bold">{myRow.total}</span> points</> : "Match recorded"}
          </p>
        </motion.section>

        <section className="flex items-end justify-center gap-4 sm:gap-6">
          <PodiumSpot rank={2} player={podium[2]} />
          <PodiumSpot rank={1} player={podium[1]} />
          <PodiumSpot rank={3} player={podium[3]} />
        </section>

        <section className="text-left max-w-md mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2.5">all players</div>
          <ul data-testid="results-list" className="space-y-2">
            {lb.map((r) => (
              <motion.li
                key={r.user_id}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                  r.user_id === user?.id ? "bg-[var(--accent)]/10 border border-[var(--accent)]/30" : "bg-[var(--surface)] border border-white/5"
                }`}
              >
                <span className="text-zinc-500 w-6 text-right font-mono">#{r.rank}</span>
                <UserName user={r} className="flex-1 font-semibold truncate" />
                <span className="font-mono font-bold tabular-nums">{r.total}</span>
              </motion.li>
            ))}
          </ul>
        </section>

        <section className="flex flex-wrap justify-center gap-3">
          {isHost && !isRanked && (
            <button
              onClick={playAgain}
              disabled={busy}
              data-testid="results-play-again"
              className="px-6 py-3 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 transition inline-flex items-center gap-2 shadow-[0_8px_30px_-8px_var(--accent)] disabled:opacity-60"
            >
              <RotateCcw size={16} /> {busy ? "Restarting…" : "Play again with same crew"}
            </button>
          )}
          {!isHost && !isRanked && lobby && (
            <div
              data-testid="results-waiting-for-host"
              className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-zinc-400"
            >
              Waiting for host to restart…
            </div>
          )}
          {isRanked && (
            <div
              data-testid="results-ranked-info"
              className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-zinc-400"
            >
              Ranked lobbies don't replay — head back to the queue.
            </div>
          )}
          <Link to="/dashboard" data-testid="results-back" className="px-6 py-3 rounded-full border border-white/15 hover:bg-white/5 transition inline-flex items-center gap-2">
            <Home size={16} /> Back to dashboard
          </Link>
        </section>
      </main>

      <AnimatePresence>
        {reopenBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            data-testid="reopen-banner"
            className="fixed bottom-6 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-30 rounded-2xl border border-[var(--accent)]/40 bg-[var(--surface)] shadow-2xl p-4"
          >
            <div className="text-[10px] uppercase tracking-widest text-[var(--accent)] font-bold">
              {reopenBanner.by_username} wants to play again
            </div>
            <div className="text-sm text-zinc-200 mt-1">
              Stay in the lobby for another round, or head back to the dashboard.
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={stayInLobby}
                data-testid="reopen-stay"
                className="flex-1 px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              >
                <RotateCcw size={14} /> Stay
              </button>
              <button
                onClick={leaveLobby}
                data-testid="reopen-leave"
                className="px-4 py-2 rounded-full border border-white/15 text-zinc-300 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:bg-white/5"
              >
                <LogOut size={14} /> Leave
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
